import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import { toNumber, getItemFromArray } from "../../components/Common";
import * as APP from "../../redux_store/appState";
import { ClearScratch } from "../layout/PaperDesigner";
import { updateLayout2D } from "../../redux_store/appStateHelper";
import { updateAreaSpecificationModificationArray } from "../../redux_store/jobState";

const styles = (theme) => ({
    qq2dCanvasContainer: {
        zIndex: 0,
        display: "block",
        position: "absolute",
        top: "0px",
        right: "0px",
        backgroundColor: "rgba(255, 255, 255, 255)",
        width: "100%",
        height: "100%",
        color: "#000",
        transition: "visibility 0.3s, opacity 0.3s linear",
        textAlign: "left",
    },

    qq2dCanvas: {
        width: "100vw",
        height: "100vh",
    },
    snackbar: {
        zIndex: "1300",
    },
    typography: {
        padding: "20px",
    },
    menu_item: {
        fontFamily: theme.typography.fontFamily,
        fontSize: "16px",
        "&:hover": {
            background: "#b9c9e6",
        },
    },
});

const designerStyles = makeStyles({
    iconRoot: {
        textAlign: "center",
        display: "flex",
        alignItems: "center",
    },
    paper: {
        padding: "125px 20px 20px 20px",
        minWidth: "500px",
        maxWidth: "25%",
        backgroundColor: "#EDF0F3",
    },
    mini_button: {
        position: "absolute",
        right: "10px",
        lineHeight: 1.25,
        paddingTop: "5px",
    },
});

export const GetItems = (parameters) => {
    if (window.svg && window.paper) {
        return window.paper.project.getItems(parameters);
    }
    return null;
};

const SetChildrenGrey = (item) => {
    if (!item?.children) return;
    item.children.forEach(function (item) {
        if (window.last_pop_item === item) {
            return;
        }
        if (item.fillColor) item.fillColor = "#dddddd";
        if (item.strokeColor) item.strokeColor = "#bbbbbb";
        item.pickable = false;
        if (item.children) {
            SetChildrenGrey(item);
        }
    });
};

function IsParallel(angle1, angle2) {
    if (angle1 !== undefined && angle2 !== undefined) {
        let pop_item_angle = Math.abs(toNumber(angle1));
        let this_angle = Math.abs(toNumber(angle2));
        let this_angle_minus180 = this_angle - 180;
        let this_angle_plus180 = this_angle + 180;

        if (
            Math.abs(pop_item_angle - this_angle) < 5 ||
            Math.abs(pop_item_angle - this_angle_minus180) < 5 ||
            Math.abs(pop_item_angle - this_angle_plus180) < 5
        ) {
            //lines are parallel
            return true;
        }
    }
    return false;
}

export function FormatSVGForExtendBeam(pop_item) {
    let items = GetItems();
    if (items?.[0]?.children) {
        SetChildrenGrey(items?.[0]);
    }

    if (!pop_item) {
        pop_item = window.last_pop_item;
    } else {
        window.last_pop_item = pop_item;
    }

    GetItems({ is_beam: true }).forEach(function (beam) {
        if (beam == pop_item) return;
        beam.bringToFront();
        beam.parallel = IsParallel(pop_item?.angle, beam?.angle);
        if (!beam.parallel) {
            beam.fillColor = "#448844";
            beam.strokeColor = "#448844";
            beam.strokeWidth = 4;
            beam.pickable = true;
        }
    });

    let sheet_outlines = GetItems({ is_sheet_outline: true });
    sheet_outlines.forEach(function (sheet_outline) {
        sheet_outline.parallel = IsParallel(pop_item?.angle, sheet_outline?.angle);
        if (!sheet_outline.parallel) {
            sheet_outline.strokeColor = "#4444EE";
            sheet_outline.strokeWidth = 36;
            window.paper.project.activeLayer.addChild(sheet_outline);
            sheet_outline.bringToFront();
            sheet_outline.pickable = true;
        }
    });
    GetItems({ class: window.paper.TextItem }).forEach(function (item) {
        item.sendToBack();
    });

    if (pop_item) {
        pop_item.bringToFront();
        pop_item.fillColor = "#AAEEAA";
        pop_item.strokeColor = "#88BB88";
        pop_item.strokeWidth = 12;
    }
}

export default function SpecExtendBeams(props) {
    const dispatch = useDispatch();
    const app = useSelector((state) => state.app);
    const { pop_item } = props;
    const SELECTED_ELEMENT = app.designer.designer2D[APP.SELECTED_POINT];

    const SELECT_POINT = app.designer.designer2D[APP.SELECT_POINT];
    const [waitingForPoints, setWaitingForPoints] = React.useState(false);
    const [loaded, setLoaded] = React.useState(false);
    const AreaSpecifications = useSelector((state) => state.job.AreaSpecifications);
    const areaDataBeamSpecMods = AreaSpecifications[0].BEAMSPECMODIFICATIONS;

    const filtered_areaDataBeamSpecMods = areaDataBeamSpecMods.filter((mod) => {
        return mod.Area === undefined || mod.Area === pop_item.areaIndex;
    });
    const ref = pop_item ? pop_item.ref : null;
    const ref2 = pop_item ? pop_item.ref2 : null;

    let existing_beam_spec_mods = getItemFromArray(
        filtered_areaDataBeamSpecMods,
        "Ref",
        ref,
        "Ref2",
        ref2,
        30,
        90,
        true
    );

    React.useEffect(() => {
        if (!loaded) return;
        if (SELECTED_ELEMENT && waitingForPoints) {
            dispatch(APP.update2DDesigner(APP.SELECT_POINT, false));
            let beam_spec_mods = {};
            if (existing_beam_spec_mods) {
                beam_spec_mods = existing_beam_spec_mods ? { ...existing_beam_spec_mods } : {};
            }
            beam_spec_mods.P1 = ref.split(";")[0].replaceAll("/", ",");
            beam_spec_mods.P2 = ref.split(";")[1].replaceAll("/", ",");
            beam_spec_mods.Ref = ref;
            beam_spec_mods.Ref2 = ref2 ? ref2 : "";
            beam_spec_mods.Area = pop_item.areaIndex;
            beam_spec_mods.ExtendToBeam =
                SELECTED_ELEMENT.areaIndex +
                "|" +
                (SELECTED_ELEMENT?.side_ref ? "OL_" + SELECTED_ELEMENT?.side_ref : SELECTED_ELEMENT.ref2);

            dispatch(
                updateAreaSpecificationModificationArray(
                    pop_item.areaIndex,
                    "BEAMSPECMODIFICATIONS",
                    ref,
                    null,
                    beam_spec_mods,
                    true,
                    ref2
                )
            );
        }
    }, [SELECTED_ELEMENT]);

    React.useEffect(() => {
        if (!loaded) return;
        if (SELECT_POINT === false) {
            setWaitingForPoints(false);
            dispatch(APP.updateDesigner(APP.TOOL_DRAWER_HIDE, false));
            onClose();
        }
    }, [SELECT_POINT]);

    React.useEffect(() => {
        if (!loaded && props.open) {
            extendBeam();
            setLoaded(true);
        }
    }, [props?.open]);

    const extendBeam = () => {
        setWaitingForPoints(true);
        ClearScratch();

        if (window.svg) {
            window.last_pop_item = props.pop_item;
            FormatSVGForExtendBeam(props.pop_item);
        }

        // dispatch(
        //     APP.update2DDesigner(
        //         APP.SELECT_POINT_NOTE,
        //         <>
        //             Select beam or edge to extend the beam to. <br />
        //             *Right click or press 'ESC' to finish.
        //         </>
        //     )
        // );
        dispatch(APP.updateDesigner(APP.TOOL_DRAWER_HIDE, true));
        dispatch(APP.update2DDesigner(APP.SELECTED_POINT, false));
        dispatch(APP.update2DDesigner(APP.SELECT_POINT, "SelectElement"));
    };

    const onClose = () => {
        ClearScratch();
        setLoaded(false);

        dispatch(updateLayout2D());
        if (props.onClose) {
            props.onClose();
            return;
        }
    };

    // if (!loaded && props.open) {

    //     extendBeam();
    //     setLoaded(true);
    // }

    return (
        <div
            style={{
                maxWidth: "450px",
                textAlign: "left",
                top: "100px",
                padding: "10px",
                background: "#d7751bab",
                position: "absolute",
                right: "12px",
                zIndex: "5",
                color: "white",
            }}
        >
            <>
                Select beam or edge to extend the beam to. <br />
                *Right click or press 'ESC' to finish.
            </>
        </div>
    );
}
